'use client';import{c as _c}from"react/compiler-runtime";import{usePageTracePolicy}from'@/app/contexts/TracePolicyContext';import{useInteractionArea}from'@/app/contexts/InteractionAreaContext';import{addLogToBuffer}from'@/app/utils/loggingInterval';import{EVENT_NAME_PREFIX}from'./constants';import type{InteractionEvent,AnalyticsActionType,AnalyticsClickItem}from'@/app/types/analytics';/**
 * Hook that creates a logger function for tracking user interactions.
 *
 * This hook tracks:
 * - Button clicks
 * - Form submissions
 * - Key commands
 * - Other UI interactions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const logClick = useInteractionLogger(
 *     "download_button_click",
 *     "DownloadButton",
 *     "click"
 *   );
 *
 *   return (
 *     <button onClick={() => {
 *       logClick();
 *       // ... rest of handler
 *     }}>
 *       Download
 *     </button>
 *   );
 * }
 * ```
 *
 * @example With metadata
 * ```tsx
 * function MediaItem({ id }: { id: string }) {
 *   const logClick = useInteractionLogger(
 *     "media_item_click",
 *     "MediaItem",
 *     "click",
 *     undefined,
 *     { mediaId: id }
 *   );
 *
 *   return <div onClick={logClick}>...</div>;
 * }
 * ```
 */export function useInteractionLogger(eventName,actionTarget,actionType,clickItem,metadata){const $=_c(8);const tracePolicy=usePageTracePolicy();const{targetArea}=useInteractionArea();let t0;if($[0]!==actionTarget||$[1]!==actionType||$[2]!==clickItem||$[3]!==eventName||$[4]!==metadata||$[5]!==targetArea||$[6]!==tracePolicy){t0=()=>{if(process.env.NODE_ENV==="development"&&targetArea==null){console.warn("[useInteractionLogger] Interaction event logged without a targetArea.",{eventName},"Wrap the component in an InteractionAreaProvider to set the target area.");}const prefixedEventName=eventName.startsWith(EVENT_NAME_PREFIX)?eventName:`${EVENT_NAME_PREFIX}${eventName}`;const event={actionTarget,actionType,clickItem,eventName:prefixedEventName,httpReferrer:typeof window!=="undefined"?window.location.href:"",interface:"desktop_web",metadata,targetArea:targetArea??undefined,timestamp:Date.now(),tracePolicyReferrer:tracePolicy??""};addLogToBuffer(event);if(process.env.NODE_ENV==="development"){console.debug("[useInteractionLogger] Interaction logged",{eventName:prefixedEventName,actionTarget,targetArea});}};$[0]=actionTarget;$[1]=actionType;$[2]=clickItem;$[3]=eventName;$[4]=metadata;$[5]=targetArea;$[6]=tracePolicy;$[7]=t0;}else{t0=$[7];}return t0;}/**
 * Creates an interaction event and adds it to the logging buffer.
 * Use this for one-off logging outside of React components.
 *
 * Note: The "midjen." prefix is automatically added to eventName if not present.
 *
 * @param event - The interaction event to log
 *
 * @example
 * ```ts
 * logInteractionEvent({
 *   actionTarget: "ShareButton",
 *   actionType: "click",
 *   eventName: "share_button_click", // Will become "midjen.share_button_click"
 *   httpReferrer: window.location.href,
 *   interface: "desktop_web",
 *   timestamp: Date.now(),
 *   tracePolicyReferrer: "midjen.studio",
 * });
 * ```
 */export function logInteractionEvent(event:InteractionEvent):void{// Add prefix if not already present
const prefixedEvent={...event,eventName:event.eventName.startsWith(EVENT_NAME_PREFIX)?event.eventName:`${EVENT_NAME_PREFIX}${event.eventName}`};addLogToBuffer(prefixedEvent);if(process.env.NODE_ENV==='development'){console.debug('[logInteractionEvent] Interaction logged',{event:prefixedEvent});}}